import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  InvalidFormField,
  LocationNotFound,
  PaymentsNotReady,
  StoreNotFound,
  type StoreRoleForbidden,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { Location, type LocationUpdate, type NewLocation, ShipFrom, StoreLocations } from "../model/store";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

/** `/stores/:storeId/locations`: a shop's branches, and which one parcels leave from. */
export class LocationsApi {
  constructor(private readonly t: Transport) {}

  async list(ctx: ApiContext, storeId: string): Promise<Result<StoreLocations, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/locations`);
    return forward(
      r,
      (j) => parse(StoreLocations, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Adds a branch. One per shop for now: a second is refused as `InvalidFormField` on `storeId`. */
  async create(
    ctx: ApiContext,
    storeId: string,
    location: NewLocation,
  ): Promise<Result<Location, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/locations`, { body: location });
    return forward(
      r,
      (j) => parse(Location, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId" && e.code === "doesnt-exist") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Partial edit, judged by the address it leaves behind. Coordinates are re-derived from it in the background. */
  async update(
    ctx: ApiContext,
    storeId: string,
    locationId: string,
    update: LocationUpdate,
  ): Promise<
    Result<Location, ApiNotAuthorized | StoreNotFound | LocationNotFound | StoreRoleForbidden | InvalidFormField>
  > {
    const r = await this.t.patch(ctx, `/stores/${storeId}/locations/${locationId}`, { body: update });
    return forward(
      r,
      (j) => parse(Location, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "locationId") return new LocationNotFound(locationId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Nominates the branch parcels ship from; null hands the choice back. An incomplete address is accepted and reported. */
  async setShipFrom(
    ctx: ApiContext,
    storeId: string,
    locationId: string | null,
  ): Promise<
    Result<ShipFrom, ApiNotAuthorized | StoreNotFound | LocationNotFound | StoreRoleForbidden | InvalidFormField>
  > {
    const r = await this.t.put(ctx, `/stores/${storeId}/ship-from`, { body: { locationId } });
    return forward(
      r,
      (j) => parse(ShipFrom, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "locationId") return new LocationNotFound(locationId ?? "", e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Registers the branch as a Stripe Terminal location on the store's account, so readers can be attached to it. */
  async enableTerminal(
    ctx: ApiContext,
    storeId: string,
    locationId: string,
  ): Promise<Result<Location, ApiNotAuthorized | StoreNotFound | LocationNotFound | PaymentsNotReady>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/locations/${locationId}/terminal`);
    return forward(
      r,
      (j) => parse(Location, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "locationId") return new LocationNotFound(locationId, e);
          if (e.incorrectValue === "") return new PaymentsNotReady(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }
}
